import React, {useEffect, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import Animated, {FadeIn, FadeOut, Layout} from 'react-native-reanimated';
import {ThemeProvider} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import ItemSeparator from '../../../components/ItemSeparator';
import CheckoutLogInModal from '../../../components/modals/CheckoutLogInModal';
import CouponModal from '../../../components/modals/CouponModal';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import Text from '../../../components/ui-controls/Text';
import useIsLoggedIn from '../../../hooks/useIsLoggedIn';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {CartStore} from '../../../stores/cartStore';
import {useStores} from '../../../stores/use-stores';
import theme from '../../../style/theme';
import rewardsTheme from '../../../theme/rewardsTheme';
import {Analytics} from '../../../utils/analytics/analytics';
import {
  EventName,
  EventScreenName,
  EventSourceType,
} from '../../../utils/analytics/events';
import {toAUD} from '../../../utils/functions';
import CartLineItem from '../components/CartLineItem';
import LastMinuteOptionTileCard from '../components/LastMinuteOptionTileCard';
import SectionTitle from '../components/SectionTitle';
import WebDesktopSection from '../components/WebDesktopSection';

const CartFooterButtons = observer(
  ({
    cartStore,
    onCheckoutPress,
    onCouponPress,
  }: {
    cartStore: CartStore;
    onCheckoutPress: Function;
    onCouponPress: Function;
  }) => (
    <View
      style={styles.footerButtons}
      testID="cart-screen-actions-footer-buttons">
      <Pressable style={styles.coupon} onPress={onCouponPress}>
        {!!cartStore.currentOrder?.coupon_code && (
          <Text
            adjustsFontSizeToFit
            numberOfLines={1}
            style={styles.coupon.secondLine}>
            {cartStore.currentOrder?.coupon_code}
          </Text>
        )}
        {!cartStore.currentOrder?.coupon_code && (
          <Text style={styles.coupon.noCoupon}>ADD COUPON</Text>
        )}
      </Pressable>
      <Pressable
        style={styles.checkoutButton}
        onPress={onCheckoutPress}
        disabled={!cartStore.currentOrder?.order_lines}>
        <Text style={styles.checkoutText} adjustsFontSizeToFit>
          CHECKOUT NOW
        </Text>
        <View style={styles.vertLine} />
        {!cartStore.dirtyOrder && (
          <Text style={styles.checkoutTotal}>
            {toAUD(cartStore.totalAmount)}
          </Text>
        )}
        {!!cartStore.dirtyOrder && (
          <View style={styles.activityHolder}>
            <ActivityIndicator
              style={styles.activityIndicator}
              size="small"
              color="white"
            />
          </View>
        )}
      </Pressable>
    </View>
  ),
);

export function EmptyCart({isWebDesktopScreen}: {isWebDesktopScreen: boolean}) {
  return (
    <View
      style={[
        styles.centerNotice,
        isWebDesktopScreen ? {marginTop: '25%'} : {},
      ]}
      testID="cart=screen-empty-cart">
      <Text
        style={[
          styles.largeNotice,
          {
            marginBottom: 16,
          },
        ]}>
        NO PRODUCTS FOUND
      </Text>
      <Text
        style={{
          fontSize: 16,
          fontWeight: '300',
          color: theme.colour.text.heading.primary,
          textAlign: 'center',
          fontStyle: 'italic',
        }}>
        There doesn&apos;t appear to be any
      </Text>
      <Text
        style={{
          fontSize: 16,
          fontWeight: '300',
          color: theme.colour.text.heading.primary,
          textAlign: 'center',
          fontStyle: 'italic',
        }}>
        products in your shopping cart.
      </Text>
    </View>
  );
}

const CartScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.CART>) => {
    const {cartStore, userStore} = useStores();
    const isLoggedIn = useIsLoggedIn();
    const {isWebDesktopScreen} = useWebScreenDimensions();
    const [isCouponModalVisible, setCouponModalVisible] = useState(false);
    const [isCheckoutLogInModalVisible, setCheckoutLogInModalVisible] =
      useState(false);

    useEffect(() => {
      cartStore.getProductsPromotedAtCheckout();
    }, [cartStore]);

    const totalsLine = (
      name: string,
      amount: number,
      key: string,
      forceAmount?: boolean,
    ) => {
      if (!name || (!amount && !forceAmount)) {
        return null;
      }
      return (
        <Animated.View
          entering={FadeIn.duration(500).delay(100)}
          exiting={FadeOut.duration(250)}
          key={key}
          style={styles.savingsRow}>
          <Text style={[styles.adjName, styles.adjText]}>
            <Text style={{fontStyle: 'italic', fontWeight: '600'}}>
              {name} {amount < 0 ? 'savings' : ''}
            </Text>
            :
          </Text>
          {!!cartStore.dirtyOrder && (
            <View style={styles.activityHolder}>
              <ActivityIndicator
                style={styles.activityIndicator}
                size="small"
                color={theme.colour.activityIndicator.primary}
              />
            </View>
          )}
          {!!(!cartStore.dirtyOrder && (amount || forceAmount)) && (
            <Text style={[styles.adjAmount, styles.adjText]}>
              {toAUD(amount, forceAmount)}
            </Text>
          )}
        </Animated.View>
      );
    };

    const renderAdjustments = () => {
      const {currentOrder} = cartStore;

      if (!currentOrder) {
        return null;
      }

      const adjustments = currentOrder.adjustments || [];
      const discountsAndCredits = currentOrder.discounts_and_credits || [];
      const chargeLines =
        currentOrder.order_lines?.filter((ol: any) => ol.charge) || [];

      if (
        !adjustments.length &&
        !discountsAndCredits.length &&
        !chargeLines.length
      ) {
        return null;
      }

      return (
        <Animated.View
          layout={Layout.duration(500)}
          style={styles.adjHolder}
          testID="cart-adjustments">
          {adjustments.map((adj: any, index: any) =>
            totalsLine(
              adj.name,
              adj.amount.cents_inc_tax,
              `${adj.name}_${index}`,
            ),
          )}
          {discountsAndCredits.map((dcl: any, index: any) =>
            totalsLine(dcl.narrative, dcl.amount, `${dcl.narrative}_${index}`),
          )}
          {!cartStore.switchingDeliveryPickup
            ? chargeLines.map((ol: any, index: any) =>
                totalsLine(
                  ol.name,
                  ol.total.cents_inc_tax,
                  `${ol.id}_${index}`,
                  true,
                ),
              )
            : null}
        </Animated.View>
      );
    };

    const orderLinesIds =
      cartStore.currentOrder?.order_lines?.map(
        (item: any) => item.product_id,
      ) || [];
    const lastMinuteOptionsItems = (
      cartStore.promotedAtCheckoutProducts || []
    ).filter((item: any) => !orderLinesIds.includes(item.product_id));

    const renderLastMinuteOptions = () => {
      if (!lastMinuteOptionsItems.length) return <></>;

      return isWebDesktopScreen
        ? renderLastMinuteOptionsWebDesktop(lastMinuteOptionsItems)
        : renderLastMinuteOptionsMobile(lastMinuteOptionsItems);
    };

    const renderLastMinuteOptionsWebDesktop = (
      promotedAtCheckoutProducts: any[],
    ) =>
      promotedAtCheckoutProducts.length ? (
        <View testID="checkout-screen-your-order">
          {promotedAtCheckoutProducts.map((item, index) => (
            <CartLineItem
              item={item}
              index={index}
              key={item.product_id}
              isTotalVisible={false}
            />
          ))}
        </View>
      ) : (
        <ActivityIndicator
          style={styles.activityIndicator}
          size="small"
          color={theme.colour.activityIndicator.primary}
        />
      );

    const renderLastMinuteOptionsMobile = (
      promotedAtCheckoutProducts: string | ArrayLike<any> | null | undefined,
    ) =>
      promotedAtCheckoutProducts?.length ? (
        <ThemeProvider theme={rewardsTheme}>
          <View testID="checkout-screen-your-order">
            <FlatList
              data={promotedAtCheckoutProducts}
              renderItem={({item, index}) => (
                <LastMinuteOptionTileCard
                  item={item}
                  index={index}
                  key={item.product_id}
                  isTotalVisible={false}
                />
              )}
              ItemSeparatorComponent={ItemSeparator}
              horizontal
              showsHorizontalScrollIndicator={Platform.OS === 'web'}
              ListEmptyComponent={() => (
                <ActivityIndicator
                  style={styles.activityIndicator}
                  size="small"
                  color={theme.colour.activityIndicator.primary}
                />
              )}
            />
          </View>
        </ThemeProvider>
      ) : (
        <ActivityIndicator
          style={styles.activityIndicator}
          size="small"
          color={theme.colour.activityIndicator.primary}
        />
      );

    const renderOrderItems = () => {
      const data = cartStore.currentOrder?.order_lines?.filter(
        (line: any) => !line.charge,
      );
      if (data && data.length) {
        return data.map((item: any, index: any) => (
          <CartLineItem
            item={{
              ...item,
              stickers: item?.line?.stickers,
              extra_prices: item?.line?.extra_prices,
            }}
            index={index}
            key={item.product_id}
          />
        ));
      } else return <EmptyCart isWebDesktopScreen={isWebDesktopScreen} />;
    };

    const handleCheckoutPress = () => {
      !isLoggedIn
        ? setCheckoutLogInModalVisible(true)
        : navigation.navigate(AppRoute.CART_ORDER_CHECKOUT);
      Analytics.logEvent({
        eventName: EventName.Checkout,
        parameters: {
          screen_name: EventScreenName.Cart,
          source_type: EventSourceType.Button,
          value: cartStore.currentOrder?.total?.cents_inc_tax / 100,
          tax: cartStore.currentOrder?.total?.cents_tax / 100,
          currency: 'AUD',
        },
      });
    };

    const handleCouponPress = () => setCouponModalVisible(true);

    const renderAdjustmentsAndCartActions = () => (
      <View
        style={[!isWebDesktopScreen && styles.stickyFooter]}
        testID="cart-screen-actions">
        {renderAdjustments()}
        {!!cartStore.currentOrder && (
          <View
            testID="cart-screen-actions-footer-wrapper"
            style={[
              {
                width: '100%',
                paddingBottom: cartStore.currentOrder?.adjustments?.length
                  ? 12
                  : 0,
                minHeight: cartStore.currentOrder?.adjustments?.length
                  ? undefined
                  : 60,
              },
            ]}>
            <CartFooterButtons
              cartStore={cartStore}
              onCheckoutPress={handleCheckoutPress}
              onCouponPress={handleCouponPress}
            />
          </View>
        )}
      </View>
    );

    const orderLines = cartStore.currentOrder?.order_lines?.filter(
      (line: any) => !line.charge,
    );

    if (!orderLines || !orderLines.length) {
      return (
        <ContentView testID="cart-screen">
          <EmptyCart isWebDesktopScreen={isWebDesktopScreen} />
        </ContentView>
      );
    }

    const handleLoginModalPress = () => {
      setCheckoutLogInModalVisible(false);
      userStore.setPostLoginReturnToScreen(AppRoute.CART_ORDER_CHECKOUT);
      navigation.navigate(AppRoute.ACCOUNT_NOT_LOGGED_IN);
    };

    return (
      <ContentView testID="cart-screen">
        <ScrollView
          testID="checkout-screen-scrollview"
          showsVerticalScrollIndicator={isWebDesktopScreen}
          showsHorizontalScrollIndicator={isWebDesktopScreen}>
          {isWebDesktopScreen ? (
            <View
              testID="checkout-screen-horizontal-content"
              style={styles.webDesktopHorizontalContent}>
              <WebDesktopSection title="CART">
                {renderOrderItems()}
              </WebDesktopSection>
              <WebDesktopSection title="LAST MINUTE OPTIONS">
                {renderLastMinuteOptions()}
              </WebDesktopSection>
              <WebDesktopSection title="CHECKOUT">
                {renderAdjustmentsAndCartActions()}
              </WebDesktopSection>
            </View>
          ) : (
            <>
              <SectionTitle>CART</SectionTitle>
              {renderOrderItems()}
              {!!lastMinuteOptionsItems.length && (
                <SectionTitle style={{marginTop: 8}}>
                  LAST MINUTE OPTIONS
                </SectionTitle>
              )}

              {renderLastMinuteOptions()}
            </>
          )}
        </ScrollView>
        {!isWebDesktopScreen && renderAdjustmentsAndCartActions()}
        <CouponModal
          open={isCouponModalVisible}
          callback={() => setCouponModalVisible(false)}
        />
        <CheckoutLogInModal
          isVisible={isCheckoutLogInModalVisible}
          onPress={handleLoginModalPress}
        />
      </ContentView>
    );
  },
);

export default CartScreen;

const styles = StyleSheet.create({
  webDesktopHorizontalContent: {
    flexDirection: 'row',
    gap: 16,
  },
  centerNotice: {
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch',
    marginTop: '50%',
  },
  largeNotice: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.colour.text.heading.primary,
  },
  stickyFooter: {
    marginHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
  },
  adjHolder: {
    borderTopWidth: 1,
    borderTopColor: 'grey',
    marginHorizontal: 8,
    paddingHorizontal: 8,
    backgroundColor: 'white',
    width: '100%',
  },
  savingsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    minHeight: 22,
    backgroundColor: 'white',
  },
  adjText: {
    textAlign: 'right',
    color: theme.colour.text.heading.primary,
    fontWeight: '500',
  },
  adjName: {
    flex: 3,
  },
  adjAmount: {
    flex: 1,
  },
  activityHolder: {
    flex: 1,
  },
  activityIndicator: {
    flex: 1,
  },
  footerButtons: {
    flexDirection: 'row',
    alignSelf: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  coupon: {
    flex: 1,
    marginRight: 8,
    borderRadius: 4,
    borderWidth: 0.5,
    borderColor: theme.colour.uiControls.primary,
    paddingVertical: 8,
    paddingHorizontal: 4,
    justifyContent: 'center',
    firstLine: {
      color: theme.colour.text.heading.primary,
      textAlign: 'center',
      fontSize: 8,
      fontStyle: 'italic',
    },
    secondLine: {
      color: theme.colour.text.heading.primary,
      textAlign: 'center',
      fontSize: 12,
    },
    noCoupon: {
      color: theme.colour.text.heading.primary,
      fontWeight: '600',
      fontSize: 16,
      textAlign: 'center',
    },
  },
  checkoutButton: {
    flex: 2,
    backgroundColor: theme.colour.background.button.primary,
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 4,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  checkoutText: {
    flex: 1.5,
    color: 'white',
    textAlign: 'center',
    fontWeight: '600',
  },
  vertLine: {
    borderRightColor: 'white',
    borderWidth: 0,
    borderRightWidth: 1,
    flex: 0.1,
  },
  checkoutTotal: {
    flex: 1,
    color: 'white',
    fontSize: 16,
    textAlign: 'center',
    fontWeight: '600',
  },
});
